<template>
  <div>
    <div v-if="Object.keys(visibleQuestions || {}).length">
      <div class="row">
        <div
          v-for="questionName in Object.keys(visibleQuestions || {})"
          :key="questionName"
          class="card-deck mb-4"
          :class="[
            `${widthClassesForQuestion(visibleQuestions[questionName])}`,
            `${
              visibleQuestions[questionName].type === 'freetext' &&
              'hide-fullscreen'
            }`
          ]"
        >
          <component
            :is="componentForQuestion(visibleQuestions[questionName])"
            v-bind="propsForQuestion(visibleQuestions[questionName])"
          />
        </div>
      </div>
      <div v-if="!fullScreen" class="row">
        <div class="col mt-5">
          <div
            v-if="!fullScreen && shouldShowOwnResponsesCheck"
            class="d-flex justify-content-end"
          >
            <PulsFormCheck
              v-model="shouldHideOwnResponses"
              :label="$t('categoryDetails.hideOwnAnswers')"
            />
          </div>
          <div
            v-else-if="shouldShowNotRespondedText"
            class="d-flex justify-content-start"
          >
            <p class="text-muted">
              {{ $t('categoryDetails.notParticipating') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="d-flex justify-content-center">
      <div class="alert alert-info">
        <div class="d-flex align-items-center text-info">
          <i class="bi bi-info-circle-fill text-info fs-5 me-3"></i>
          {{ $t('widgetCategoriesBars.notEnoughResponses') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuestionDetailsZeroToTen from './question-details/QuestionDetailsZeroToTen.vue'
import QuestionDetailsFreetext from './question-details/QuestionDetailsFreetext.vue'
import QuestionDetailsNotEnoughResponses from './question-details/QuestionDetailsNotEnoughResponses.vue'
import QuestionDetailsLoading from './question-details/QuestionDetailsLoading.vue'

export default {
  name: 'CategoryDetailsQuestions',
  props: {
    id: {
      type: String,
      default: ''
    },
    followUpQuestionsState: {
      type: Object,
      default: () => {}
    },
    updateFollowupQuestionStateForQuestion: {
      type: Function,
      default: () => {}
    },
    category: {
      type: Object,
      default: () => {}
    },
    userIsMemberOfSelectedGroup: {
      type: Boolean,
      default: false
    },
    isIndividualGroup: {
      type: Boolean,
      default: false
    },
    loadQuestionResponses: {
      type: Function,
      default: () => null
    },
    requestHeightCalculation: {
      type: Function,
      default: () => null
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    loadingQuestionResponses: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      questionsWithMergedFollowUp: {},
      questionsWithPreconditions: {},
      followUpFreetextQuestionsForQuestion: {},
      visiblePreconditions: {},
      shouldHideOwnResponses: true
    }
  },
  computed: {
    anyQuestionHasHistory() {
      return Object.values(this.visibleQuestions || {}).some((q) => {
        return (q.averages || []).length > 1
      })
    },
    userHasRespondedToQuestions() {
      return Object.values(this.visibleQuestions || {}).find((q) => {
        return q.hasOwnProperty('ownResponse')
      })
    },
    shouldShowOwnResponsesCheck() {
      const anyQuestionHasMoreThanOneResponse = Object.values(
        this.visibleQuestions || {}
      ).find((q) => {
        return q.numberOfResponses > 1
      })

      return (
        anyQuestionHasMoreThanOneResponse && this.userHasRespondedToQuestions
      )
    },
    shouldShowNotRespondedText() {
      return (
        this.userIsMemberOfSelectedGroup && !this.userHasRespondedToQuestions
      )
    },
    visibleQuestions() {
      return Object.entries(this.category?.questions || {}).reduce(
        (result, [questionName, question]) => {
          const { precondition } = question
          const questionHasNotEnoughResponsesError =
            question.error && question.error.code === 'NOT_ENOUGH_RESPONSES'
          const questionHasValues =
            questionHasNotEnoughResponsesError || question.value !== undefined
          const questionIsFreetext = question.type === 'freetext'
          const questionHasRanges = question.hasOwnProperty('rangeKeys')
          const questionIsConditionallyVisible =
            this.fullScreen ||
            !precondition ||
            this.visiblePreconditions[precondition]
          if (
            this.questionsWithMergedFollowUp[precondition] &&
            questionIsConditionallyVisible
          ) {
            if (questionIsFreetext || questionHasRanges) {
              result[questionName] = question
            } else {
              const key = `${precondition}-merged`
              result[key] = {
                ...result[key],
                parentContainerId: this.id,
                averages: [],
                name: `${this.category.questions[precondition].name}-merged`,
                messages: this.category.questions[precondition].messages,
                type: 'merged',
                yesIsBad: this.category.questions[precondition].yesIsBad,
                isHarassmentQuestion:
                  this.category.questions[precondition].isHarassmentQuestion,
                numberOfPeopleSelectedYes:
                  this.category.questions[precondition]
                    .numberOfPeopleSelectedYes,
                numberOfSensitiveQuestions:
                  this.category.questions[precondition]
                    .numberOfSensitiveQuestions,
                mergedQuestions: {
                  ...result[key]?.mergedQuestions,
                  [question.name]: {
                    averages: question.averages,
                    type: question.type,
                    yesIsBad: question.yesIsBad,
                    amount_0: question.amount_0 || 0,
                    amount_10: question.amount_10 || 0,
                    messages: question.messages,
                    name: question.name
                  }
                }
              }
            }
          } else if (
            (questionHasValues || questionIsFreetext) &&
            questionIsConditionallyVisible
          ) {
            result[questionName] = question
          }
          return result
        },
        {}
      )
    }
  },
  watch: {
    category(newValue, oldValue) {
      if (
        (!oldValue && newValue) ||
        (oldValue && newValue && oldValue.name !== newValue.name)
      ) {
        this.setFollowUpQuestionsForCategory()
      }
    }
  },
  mounted() {
    if (this.followUpQuestionsState) {
      if (this.followUpQuestionsState.questionsWithMergedFollowUp) {
        this.questionsWithMergedFollowUp =
          this.followUpQuestionsState.questionsWithMergedFollowUp
      }
      if (this.followUpQuestionsState.visiblePreconditions) {
        this.visiblePreconditions =
          this.followUpQuestionsState.visiblePreconditions
      }
    }
    this.setFollowUpQuestionsForCategory()
  },
  methods: {
    widthClassesForQuestion(question) {
      const widthClasses =
        question.type === 'merged' &&
        Object.keys(question.mergedQuestions)?.length > 2
          ? {
              fullScreen: 'col col-sm-6 col-md-6 col-xxl-6',
              default: 'col col-sm-12 col-md-12 col-xxl-8'
            }
          : {
              fullScreen: 'col col-sm-3 col-md-3 col-xxl-3',
              default: 'col col-sm-12 col-md-6 col-xxl-4'
            }
      return this.fullScreen ? widthClasses.fullScreen : widthClasses.default
    },
    handleLoadQuestionResponses(question) {
      if (question.precondition) {
        this.updateFollowupQuestionStateForQuestion({
          questionName: question.precondition,
          followUpQuestionsState: {
            questionsWithMergedFollowUp: this.questionsWithMergedFollowUp,
            visiblePreconditions: this.visiblePreconditions
          }
        })
      }
      this.loadQuestionResponses(question)
    },
    propsForQuestion(question) {
      const shouldShowFollowUpCheckbox =
        this.questionsWithPreconditions.hasOwnProperty(question.name) &&
        this.questionsWithPreconditions[question.name] === true
      const result = {
        question,
        shouldShowFollowUpCheckbox,
        followUpQuestionsState: this.followUpQuestionsState?.[question.name],
        fullScreen: this.fullScreen,
        isIndividualGroup: this.isIndividualGroup,
        parentContainerId: this.id,
        shouldHideOwnResponses: this.shouldHideOwnResponses,
        didSelectShowFollowupQuestions: this.didSelectShowFollowupQuestions,
        shouldShowMergeCheckbox:
          this.questionsWithMergedFollowUp.hasOwnProperty(question.name),
        didSelectMergeFollowupQuestions: this.didSelectMergeFollowupQuestions,
        followUpFreetextQuestionsForQuestion:
          this.followUpFreetextQuestionsForQuestion[question.name],
        anyQuestionHasHistory: this.anyQuestionHasHistory
      }

      if (question.type === 'freetext') {
        result.loadQuestionResponses = this.handleLoadQuestionResponses
      }
      return result
    },
    setFollowUpQuestionsForCategory() {
      const preconditionQuestions = {}
      const questionsWithMergedFollowUp = {}
      const numberOfPreconditionalQuestionsPerQuestion = {}
      const numberOfFollowupQuestionsRequiredToMerge = 2
      const followUpFreetextQuestionsForQuestion = {}
      if (this.category?.questions) {
        Object.values(this.category.questions).forEach((question) => {
          if (question.precondition) {
            preconditionQuestions[question.precondition] = true

            const questionWithPreconditionWillBeMerged =
              question.type !== 'freetext' && !question.rangeKeys
            if (questionWithPreconditionWillBeMerged) {
              numberOfPreconditionalQuestionsPerQuestion[
                question.precondition
              ] =
                (numberOfPreconditionalQuestionsPerQuestion[
                  question.precondition
                ] || 0) + 1
            }

            if (question.type === 'freetext') {
              followUpFreetextQuestionsForQuestion[question.precondition] = {
                ...followUpFreetextQuestionsForQuestion[question.precondition],
                [question.name]: true
              }
            }
          }
        })
      }

      Object.keys(numberOfPreconditionalQuestionsPerQuestion).forEach(
        (questionName) => {
          if (
            numberOfPreconditionalQuestionsPerQuestion[questionName] >
            numberOfFollowupQuestionsRequiredToMerge
          ) {
            questionsWithMergedFollowUp[questionName] = true
            // if (!followUpFreetextQuestionsForQuestion[questionName]) {
            // }
          }
        }
      )

      this.questionsWithPreconditions = preconditionQuestions
      this.questionsWithMergedFollowUp = questionsWithMergedFollowUp
      this.followUpFreetextQuestionsForQuestion =
        followUpFreetextQuestionsForQuestion
      this.visiblePreconditions = {}
    },
    componentForQuestion(question) {
      if (question.error?.code === 'NOT_ENOUGH_RESPONSES') {
        return QuestionDetailsNotEnoughResponses
      }
      if (this.loadingQuestionResponses?.[question.name]) {
        return QuestionDetailsLoading
      }
      switch (question.type) {
        case 'zeroToTen':
          return QuestionDetailsZeroToTen
        case 'freetext':
          return QuestionDetailsFreetext
        default:
          return QuestionDetailsZeroToTen
      }
    },
    didSelectShowFollowupQuestions({ question, value }) {
      this.visiblePreconditions[question.name] = value
      this.handleUpdateFollowupQuestionStateForQuestion(question)
      this.requestHeightCalculation()
    },
    didSelectMergeFollowupQuestions({ question, value }) {
      this.questionsWithMergedFollowUp[question.name] = value
      this.handleUpdateFollowupQuestionStateForQuestion(question)
      this.requestHeightCalculation()
    },
    handleUpdateFollowupQuestionStateForQuestion(question) {
      this.updateFollowupQuestionStateForQuestion({
        questionName: question.name,
        followUpQuestionsState: {
          questionsWithMergedFollowUp: this.questionsWithMergedFollowUp,
          visiblePreconditions: this.visiblePreconditions
        }
      })
    }
  }
}
</script>
